import React, { useId, useMemo } from "react";
import { formatPeakHour, maxSeriesValue } from "../../utils/viewersDeskHelpers";

const CHART_W = 280;
const CHART_H = 96;
const PAD = 8;

/** Smooth area + line for 7-day recorded views. `highlightIndex` marks the best day. */
export const DailyTrendAreaChart = ({ items, ariaLabel, highlightIndex = null }) => {
  const gradId = useId();
  const max = maxSeriesValue(items.map((item) => item.value));

  const points = useMemo(() => {
    if (max <= 0) return [];
    const innerW = CHART_W - PAD * 2;
    const innerH = CHART_H - PAD * 2;
    return items.map((item, index) => {
      const x = PAD + (index / Math.max(items.length - 1, 1)) * innerW;
      const y = PAD + innerH - (item.value / max) * innerH;
      return { x, y, ...item };
    });
  }, [items, max]);

  if (max <= 0 || !points.length) return null;

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(1)} ${CHART_H - PAD} L ${points[0].x.toFixed(1)} ${CHART_H - PAD} Z`;

  return (
    <div className="bd-audience-viz bd-audience-viz-trend">
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        className="bd-audience-trend-svg"
        role="img"
        aria-label={ariaLabel}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#64b5f6" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#1e88e5" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((ratio) => (
          <line
            key={ratio}
            x1={PAD}
            y1={PAD + (CHART_H - PAD * 2) * ratio}
            x2={CHART_W - PAD}
            y2={PAD + (CHART_H - PAD * 2) * ratio}
            className="bd-audience-trend-grid"
          />
        ))}
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={linePath} className="bd-audience-trend-line" fill="none" />
        {points.map((p, index) =>
          p.value > 0 ? (
            <circle
              key={p.key}
              cx={p.x}
              cy={p.y}
              r={highlightIndex === index ? 4.5 : 3.5}
              className={
                highlightIndex === index
                  ? "bd-audience-trend-dot aud-trend-dot-best"
                  : "bd-audience-trend-dot"
              }
            >
              <title>
                {highlightIndex === index
                  ? `${p.label}: ${p.value} views (best day)`
                  : `${p.label}: ${p.value} views`}
              </title>
            </circle>
          ) : null
        )}
      </svg>
      <div className="bd-audience-trend-labels">
        {items.map((item) => (
          <span key={item.key}>{item.label}</span>
        ))}
      </div>
    </div>
  );
};

/** 24-cell intensity strip from recorded hourly counts. */
export const HourlyHeatStrip = ({
  items,
  peakIndex = null,
  ariaLabel,
  tone = "default",
  unitLabel = "views",
}) => {
  const max = maxSeriesValue(items.map((item) => item.value));
  if (max <= 0) return null;

  const toneClass =
    tone === "follow"
      ? "bd-audience-heat-tone-follow"
      : tone === "unfollow"
      ? "bd-audience-heat-tone-unfollow"
      : "";

  return (
    <div className={`bd-audience-viz bd-audience-viz-heat ${toneClass}`.trim()} role="img" aria-label={ariaLabel}>
      <div className="bd-audience-heat-grid">
        {items.map((item, index) => {
          const intensity = item.value / max;
          const isPeak = peakIndex != null && index === peakIndex;
          return (
            <div
              key={item.key}
              className={`bd-audience-heat-cell ${isPeak ? "bd-audience-heat-cell-peak" : ""}`}
              style={{ "--heat": intensity }}
              title={`${formatPeakHour(index)} · ${item.value} ${unitLabel}`}
            >
              <span className="bd-audience-heat-fill" />
              {item.value > 0 ? (
                <em className="tabular-nums">{item.value}</em>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="bd-audience-heat-axis">
        <span>00</span>
        <span>06</span>
        <span>12</span>
        <span>18</span>
        <span>23</span>
      </div>
    </div>
  );
};

/** Donut split from stored unique vs repeat viewer counts. */
export const AudienceMixDonut = ({ firstTime, repeat, unique, compact = false }) => {
  const uid = useId();
  const total = firstTime + repeat;
  if (total <= 0) return null;

  const size = compact ? 88 : 112;
  const stroke = compact ? 9 : 11;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const firstShare = firstTime / total;
  const repeatShare = repeat / total;
  const firstLen = c * firstShare;
  const repeatLen = c * repeatShare;
  const firstPct = Math.round(firstShare * 100);
  const repeatPct = 100 - firstPct;

  return (
    <div className={`bd-audience-viz bd-audience-viz-donut${compact ? " bd-audience-viz-compact" : ""}`}>
      <div
        className="bd-audience-donut-wrap"
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={stroke}
          />
          {firstTime > 0 ? (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={`url(#${uid}-first)`}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${firstLen} ${c - firstLen}`}
              strokeDashoffset={0}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          ) : null}
          {repeat > 0 ? (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={`url(#${uid}-repeat)`}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${repeatLen} ${c - repeatLen}`}
              strokeDashoffset={-firstLen}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          ) : null}
          <defs>
            <linearGradient id={`${uid}-first`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#64b5f6" />
              <stop offset="100%" stopColor="#1e88e5" />
            </linearGradient>
            <linearGradient id={`${uid}-repeat`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#90caf9" />
              <stop offset="100%" stopColor="#42a5f5" />
            </linearGradient>
          </defs>
        </svg>
        <div className="bd-audience-donut-center">
          <strong className="tabular-nums">{unique}</strong>
          <span>unique</span>
        </div>
      </div>
      <ul className="bd-audience-donut-legend">
        <li>
          <i className="bd-audience-dot bd-audience-dot-unique" />
          First visit
          <strong className="tabular-nums">{firstTime}</strong>
          <em>{firstPct}%</em>
        </li>
        <li>
          <i className="bd-audience-dot bd-audience-dot-repeat" />
          Repeat
          <strong className="tabular-nums">{repeat}</strong>
          <em>{repeatPct}%</em>
        </li>
      </ul>
    </div>
  );
};

/** Ranked horizontal bars from stored per-user view counts. */
export const TopViewersRanked = ({ rows }) => {
  if (!rows.length) return null;
  const max = maxSeriesValue(rows.map((row) => row.count));

  return (
    <ul className="bd-audience-viz bd-audience-viz-ranked">
      {rows.map((row, index) => {
        const width = max > 0 ? Math.round((row.count / max) * 100) : 0;
        return (
          <li key={row.userId || index}>
            <div className="bd-audience-ranked-head">
              <span className="bd-audience-top-rank">{index + 1}</span>
              <span className="bd-audience-top-name">{row.username || "Viewer"}</span>
              <strong className="tabular-nums">{row.count}</strong>
            </div>
            <div className="bd-audience-ranked-track">
              <span className="bd-audience-ranked-fill" style={{ width: `${width}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
};
